import { Component, Input, Output, EventEmitter, ChangeDetectionStrategy, OnInit, OnDestroy, ChangeDetectorRef, AfterViewInit, ViewChild, ElementRef, TemplateRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { NzCardModule } from 'ng-zorro-antd/card';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { NzTooltipModule } from 'ng-zorro-antd/tooltip';
import { NzDividerModule } from 'ng-zorro-antd/divider';
import { NzEmptyModule } from 'ng-zorro-antd/empty';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzBadgeModule } from 'ng-zorro-antd/badge';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzGridModule } from 'ng-zorro-antd/grid';
import { NzTypographyModule } from 'ng-zorro-antd/typography';
import { NzCollapseModule } from 'ng-zorro-antd/collapse';
import { NzModalModule, NzModalService } from 'ng-zorro-antd/modal';
import { NzDescriptionsModule } from 'ng-zorro-antd/descriptions';
import { NzMessageService } from 'ng-zorro-antd/message';
import { TranslateModule } from '@ngx-translate/core';
import { Subject, takeUntil, debounceTime, distinctUntilChanged } from 'rxjs';
import { ScreenTemplate, TemplateMetadata, TemplateFilters } from '../interfaces/template.models';
import { NgZone } from '@angular/core';

@Component({
  selector: 'app-template-selector',
  templateUrl: './template-selector.component.html',
  styleUrls: ['./template-selector.component.scss'],
  changeDetection: ChangeDetectionStrategy.Default,
  standalone: false,
})
export class TemplateSelectorComponent implements OnInit, OnDestroy, AfterViewInit {
  @Input() set templates(value: ScreenTemplate[]) {
    
    this._templates = value || [];
    
    // Se temos templates, inicializar filteredTemplates IMEDIATAMENTE
    if (this._templates && this._templates.length > 0) {
    
      this.filteredTemplates = [...this._templates];
      
      // Forçar detecção de mudanças IMEDIATAMENTE
      this.ngZone.run(() => {
        this.cdr.detectChanges();
    
        
        // Verificação adicional após um tick
        setTimeout(() => {
          if (this.filteredTemplates.length === 0) {
    
            this.filteredTemplates = [...this._templates];
            this.cdr.detectChanges();
    
          }
        }, 0);
      });
    } else {
      
      this.filteredTemplates = [];
    }
  }
  
  get templates(): ScreenTemplate[] {
    return this._templates;
  }
  
  private _templates: ScreenTemplate[] = [];
  @Input() loading = false;
  @Output() templateSelected = new EventEmitter<ScreenTemplate>();
  @Output() templateApplied = new EventEmitter<ScreenTemplate>();
  @Output() templatePreviewed = new EventEmitter<ScreenTemplate>();
  @Output() templateDeleted = new EventEmitter<number>();
  @Output() templateDuplicated = new EventEmitter<{ idTemplate: number; newName: string }>();

  // Estados do componente
  filteredTemplates: ScreenTemplate[] = [];
  selectedCategory: string = '';
  searchTerm: string = '';
  sortBy: string = 'name';
  sortOrder: 'asc' | 'desc' = 'asc';
  showPreviewModal = false;
  previewTemplate: ScreenTemplate | null = null;
  duplicateTemplateName: string = '';
  currentTemplateForDuplication: ScreenTemplate | null = null;

  // ViewChild para acessar o modal
  @ViewChild('templateSelectorModal', { static: false }) templateSelectorModal!: ElementRef;
  @ViewChild('duplicateModalContent', { static: false }) duplicateModalContent!: TemplateRef<any>;

  // Propriedade para acessar Array no template
  Array = Array;

  // Filtros disponíveis
  categories = [
    { value: 'product', label: 'Produtos' },
    { value: 'partner', label: 'Parceiros' },
    { value: 'marketplace', label: 'Marketplaces' },
  ];

  sortOptions = [
    { value: 'name', label: 'Nome' },
    { value: 'createdAt', label: 'Data de Criação' },
    { value: 'updatedAt', label: 'Data de Atualização' },
  ];

  private destroy$ = new Subject<void>();

  constructor(
    private message: NzMessageService,
    private cdr: ChangeDetectorRef,
    private ngZone: NgZone,
    private modal: NzModalService
  ) {}

  ngOnInit(): void {
    // Inicializar filteredTemplates se já temos templates
    if (this.templates && this.templates.length > 0) {
      this.filteredTemplates = [...this.templates];
    }
    
    this.setupSearchDebounce();
    
    // Forçar detecção de mudanças após inicialização
    setTimeout(() => {
      this.cdr.detectChanges();
      
      // Se ainda não temos filteredTemplates, tentar inicializar novamente
      if (this.filteredTemplates.length === 0 && this.templates.length > 0) {
        this.filteredTemplates = [...this.templates];
        this.cdr.detectChanges();
      }
    }, 100);
    
    // Verificação adicional após 500ms
    setTimeout(() => {
      if (this.filteredTemplates.length === 0 && this.templates.length > 0) {
        this.forceReloadTemplates();
      }
    }, 500);
  }

  ngAfterViewInit(): void {
    // Forçar detecção de mudanças após a view estar pronta
    setTimeout(() => {
      this.cdr.detectChanges();
      
      // VERIFICAÇÃO CRÍTICA: Se temos templates mas filteredTemplates está vazio
      if (this.templates && this.templates.length > 0 && this.filteredTemplates.length === 0) {
        this.filteredTemplates = [...this.templates];
        this.cdr.detectChanges();
      }
    }, 100);
    
    // Verificação adicional após 300ms
    setTimeout(() => {
      if (this.filteredTemplates.length === 0 && this.templates.length > 0) {
        this.forceReloadTemplates();
      }
    }, 300);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Configura debounce para busca
   */
  private setupSearchDebounce(): void {
    // Implementar se necessário para busca em tempo real
  }

  /**
   * Filtra templates com base nos critérios
   */
  filterTemplates(): void {
    let filtered = [...this.templates];

    // Filtro por categoria
    if (this.selectedCategory) {
      filtered = filtered.filter(t => t.category === this.selectedCategory);
    }

    // Filtro por busca
    if (this.searchTerm) {
      const search = this.searchTerm.toLowerCase();
      filtered = filtered.filter(t => 
        t.name.toLowerCase().includes(search) || 
        t.description.toLowerCase().includes(search)
      );
    }

    // Ordenação
    if (this.sortBy) {
      filtered.sort((a, b) => {
        let aValue: any, bValue: any;

        switch (this.sortBy) {
          case 'name':
            aValue = a.name;
            bValue = b.name;
            break;
          case 'createdAt':
            aValue = a.createdAt || new Date(0);
            bValue = b.createdAt || new Date(0);
            break;
          case 'updatedAt':
            aValue = a.updatedAt || new Date(0);
            bValue = b.updatedAt || new Date(0);
            break;
          default:
            return 0;
        }

        if (this.sortOrder === 'desc') {
          return bValue > aValue ? 1 : -1;
        }
        return aValue > bValue ? 1 : -1;
      });
    }

    this.filteredTemplates = filtered;
    
    // Forçar detecção de mudanças após filtrar
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
  }

  /**
   * Limpa todos os filtros
   */
  clearFilters(): void {
    this.selectedCategory = '';
    this.searchTerm = '';
    this.sortBy = 'name';
    this.sortOrder = 'asc';
    this.filterTemplates();
  }

  /**
   * Seleciona template para preview
   */
  onTemplateSelect(template: ScreenTemplate): void {
    this.templateSelected.emit(template);
  }

  /**
   * Aplica template selecionado
   */
  applyTemplate(template: ScreenTemplate): void {
    try {
      if (!this.validateTemplate(template)) {
        this.message.error('Template inválido. Verifique a configuração.');
        return;
      }

      const translatedTemplate = this.translateTemplate(template);
      this.templateApplied.emit(translatedTemplate);
      this.message.success(`Template "${template.name}" aplicado com sucesso!`);
    } catch (error) {
      console.error('Erro ao aplicar template:', error);
      this.message.error(`Erro ao aplicar template: ${error instanceof Error ? error.message : 'Erro desconhecido'}`);
    }
  }

  /**
   * Emitir evento de exclusão de template
   */
  onDeleteTemplate(templateId: number, event: MouseEvent): void {
    event.stopPropagation();
    this.templateDeleted.emit(templateId);
  }

  /**
   * Duplicar template - solicita novo nome via modal
   */
  onDuplicateTemplate(template: ScreenTemplate, event: MouseEvent): void {
    event.stopPropagation();
    
    // Armazenar template e nome sugerido
    this.currentTemplateForDuplication = template;
    this.duplicateTemplateName = `${template.name} (Cópia)`;

    // Criar modal com template Angular
    const modal = this.modal.create({
      nzTitle: 'Duplicar Template',
      nzContent: this.duplicateModalContent,
      nzOkText: 'Duplicar',
      nzCancelText: 'Cancelar',
      nzOnOk: () => this.confirmDuplicate()
    });
  }

  /**
   * Confirma a duplicação do template
   */
  confirmDuplicate(): boolean {
    const newName = this.duplicateTemplateName?.trim();
    
    if (!newName) {
      this.message.error('Por favor, digite um nome para o template');
      return false;
    }

    if (!this.currentTemplateForDuplication?.idTemplate) {
      this.message.error('Template inválido para duplicação');
      return false;
    }

    this.templateDuplicated.emit({
      idTemplate: this.currentTemplateForDuplication.idTemplate,
      newName: newName
    });

    this.message.success('Duplicando template...');
    
    // Limpar variáveis
    this.currentTemplateForDuplication = null;
    this.duplicateTemplateName = '';
    
    return true;
  }

  /**
   * Validação básica do template
   * Aceita DOIS formatos:
   * 1. Formato ANTIGO: { components: [...] }
   * 2. Formato NOVO: { tabs: [...], panels: [...], fields: [...] }
   */
  private validateTemplate(template: ScreenTemplate): boolean {
    if (!template) {
      return false;
    }

    // Formato NOVO: Valida estrutura tabs/panels/fields/rows
    const hasNewFormat = (template as any).tabs && (template as any).panels;
    if (hasNewFormat) {
      return Array.isArray((template as any).tabs) && (template as any).tabs.length > 0 &&
        Array.isArray((template as any).panels) && (template as any).panels.length > 0;
    }

    // Formato ANTIGO: Valida array components
    if (!template.components || template.components.length === 0) {
      return false;
    }

    return template.components.every(component => 
      component.type &&
      component.props &&
      component.position &&
      component.position.row !== undefined &&
      component.position.col !== undefined
    );
  }

  /**
   * Verifica se o template é válido (método público para uso no template)
   */
  isTemplateValid(template: ScreenTemplate): boolean {
    return this.validateTemplate(template);
  }

  /**
   * Abre modal de preview do template
   */
  openPreviewModal(template: ScreenTemplate): void {
    this.previewTemplate = template;
    this.showPreviewModal = true;
    this.templatePreviewed.emit(template);
  }

  /**
   * Fecha modal de preview
   */
  closePreviewModal(): void {
    this.showPreviewModal = false;
    this.previewTemplate = null;
  }

  /**
   * Obtém ícone para categoria
   */
  getCategoryIcon(category: string): string {
    switch (category) {
      case 'product':
        return 'shopping-cart';
      case 'partner':
        return 'team';
      case 'marketplace':
        return 'shop';
      default:
        return 'file-text';
    }
  }

  /**
   * Obtém cor para categoria
   */
  getCategoryColor(category: string): string {
    switch (category) {
      case 'product':
        return 'blue';
      case 'partner':
        return 'green';
      case 'marketplace':
        return 'orange';
      default:
        return 'default';
    }
  }

  /**
   * Obtém label traduzido para categoria
   */
  getCategoryLabel(category: string): string {
    const categoryObj = this.categories.find(c => c.value === category);
    return categoryObj ? categoryObj.label : category;
  }

  /**
   * Formata data para exibição
   */
  formatDate(date: Date | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString('pt-BR');
  }

  /**
   * Obtém número de componentes do template
   */
  getComponentCount(template: ScreenTemplate): number {
    return template.components.length;
  }

  /**
   * Verifica se template tem botão de cadastro
   */
  hasRegisterButton(template: ScreenTemplate): boolean {
    return template.components.some(c => c.type === 'button');
  }

  /**
   * Obtém tipos de componentes únicos
   */
  getUniqueComponentTypes(template: ScreenTemplate): string[] {
    const types = template.components.map(c => c.type);
    return [...new Set(types)];
  }

  /**
   * Obtém grid config para exibição
   */
  getGridConfig(template: ScreenTemplate): string {
    const { columns, rows, gap } = template.gridConfig;
    return `${columns}×${rows} colunas, gap: ${gap}`;
  }

  /**
   * Obtém ícone para tipo de componente
   */
  getComponentIcon(type: string): string {
    switch (type) {
      case 'text':
        return 'font-size';
      case 'textarea':
        return 'file-text';
      case 'number':
        return 'calculator';
      case 'switch':
        return 'swap';
      case 'button':
        return 'play-circle';
      case 'tabs':
        return 'appstore';
      case 'simpletable':
        return 'table';
      case 'table':
        return 'table';
      default:
        return 'appstore';
    }
  }

  /**
   * Obtém timestamp atual para debug
   */
  getCurrentTime(): string {
    return new Date().toLocaleTimeString();
  }

  /**
   * Força recarregamento dos templates (método de debug)
   */
  forceReloadTemplates(): void {
    // Re-inicializar filteredTemplates
    if (this.templates && this.templates.length > 0) {
      this.filteredTemplates = [...this.templates];
    } else {
      this.filteredTemplates = [];
    }
    
    // Forçar detecção de mudanças
    this.ngZone.run(() => {
      this.cdr.detectChanges();
    });
  }

  /**
   * Método público para forçar atualização (pode ser chamado externamente)
   */
  public forceUpdate(): void {
    this.forceReloadTemplates();
  }

  /**
   * Traduz as chaves i18n do template para texto legível
   */
  private translateTemplate(template: ScreenTemplate): ScreenTemplate {
    const translated = { ...template };
    
    // Traduzir componentes
    translated.components = template.components.map(component => {
      const translatedComponent = { ...component };
      
      // Traduzir props
      if (component.props) {
        translatedComponent.props = { ...component.props };
        
        // Traduzir label
        if (component.props.label) {
          translatedComponent.props.label = this.translateKey(component.props.label);
        }
        
        // Traduzir placeholder
        if (component.props.placeholder) {
          translatedComponent.props.placeholder = this.translateKey(component.props.placeholder);
        }
        
        // Traduzir outros campos de texto
        if (component.props.checkedChildren) {
          translatedComponent.props.checkedChildren = this.translateKey(component.props.checkedChildren);
        }
        
        if (component.props.unCheckedChildren) {
          translatedComponent.props.unCheckedChildren = this.translateKey(component.props.unCheckedChildren);
        }
      }
      
      return translatedComponent;
    });
    
    return translated;
  }

  /**
   * Traduz uma chave i18n para texto legível
   */
  private translateKey(key: string): string {
    // Mapeamento de chaves para texto em português
    const translations: { [key: string]: string } = {
      // Product translations
      'product.name': 'Nome do Produto',
      'product.name.placeholder': 'Digite o nome do produto',
      'product.description': 'Descrição',
      'product.description.placeholder': 'Descreva o produto',
      'product.price': 'Preço',
      'product.price.placeholder': '0,00',
      'product.stock': 'Estoque',
      'product.stock.placeholder': 'Quantidade disponível',
      'product.category': 'Categoria',
      'product.category.placeholder': 'Categoria do produto',
      'product.active': 'Produto Ativo',
      'product.active.yes': 'Sim',
      'product.active.no': 'Não',
      'product.sku': 'SKU',
      'product.sku.placeholder': 'Código do produto',
      'product.brand': 'Marca',
      'product.brand.placeholder': 'Marca do produto',
      'product.weight': 'Peso (kg)',
      'product.weight.placeholder': '0,00',
      'product.dimensions': 'Dimensões',
      'product.dimensions.placeholder': 'L x A x P (cm)',
      'product.color': 'Cor',
      'product.color.placeholder': 'Cor principal',
      'product.supplier': 'Fornecedor',
      'product.supplier.placeholder': 'Nome do fornecedor',
      'product.minStock': 'Estoque Mínimo',
      'product.minStock.placeholder': '0',
      'product.notes': 'Observações',
      'product.notes.placeholder': 'Observações adicionais',
      'product.register': 'Cadastrar Produto',
      
      // Partner translations
      'partner.name': 'Nome do Parceiro',
      'partner.name.placeholder': 'Nome completo do parceiro',
      'partner.cnpj': 'CNPJ',
      'partner.cnpj.placeholder': '00.000.000/0000-00',
      'partner.type': 'Tipo de Parceiro',
      'partner.type.placeholder': 'Fornecedor, Cliente, etc.',
      'partner.email': 'Email',
      'partner.email.placeholder': 'email@exemplo.com',
      'partner.phone': 'Telefone',
      'partner.phone.placeholder': '(11) 99999-9999',
      'partner.contactPerson': 'Pessoa de Contato',
      'partner.contactPerson.placeholder': 'Nome da pessoa de contato',
      'partner.contactPhone': 'Telefone do Contato',
      'partner.contactPhone.placeholder': '(11) 99999-9999',
      'partner.address': 'Endereço',
      'partner.address.placeholder': 'Endereço completo',
      'partner.city': 'Cidade',
      'partner.city.placeholder': 'Nome da cidade',
      'partner.state': 'Estado',
      'partner.state.placeholder': 'Nome do estado',
      'partner.cep': 'CEP',
      'partner.cep.placeholder': '00000-000',
      'partner.country': 'País',
      'partner.country.placeholder': 'Nome do país',
      'partner.website': 'Website',
      'partner.website.placeholder': 'https://exemplo.com',
      'partner.creditLimit': 'Limite de Crédito',
      'partner.creditLimit.placeholder': '0,00',
      'partner.paymentTerms': 'Condições de Pagamento',
      'partner.paymentTerms.placeholder': '30/60/90 dias',
      'partner.notes': 'Observações',
      'partner.notes.placeholder': 'Observações adicionais',
      'partner.active': 'Parceiro Ativo',
      'partner.active.yes': 'Sim',
      'partner.active.no': 'Não',
      'partner.register': 'Cadastrar Parceiro',
      
      // Marketplace translations
      'marketplace.name': 'Nome do Marketplace',
      'marketplace.name.placeholder': 'Nome do marketplace',
      'marketplace.url': 'URL',
      'marketplace.url.placeholder': 'https://exemplo.com',
      'marketplace.category': 'Categoria',
      'marketplace.category.placeholder': 'Eletrônicos, Roupas, etc.',
      'marketplace.country': 'País',
      'marketplace.country.placeholder': 'Brasil',
      'marketplace.currency': 'Moeda',
      'marketplace.currency.placeholder': 'BRL, USD, EUR',
      'marketplace.commission': 'Comissão (%)',
      'marketplace.commission.placeholder': '0,00',
      'marketplace.contactEmail': 'Email de Contato',
      'marketplace.contactEmail.placeholder': 'contato@marketplace.com',
      'marketplace.contactPhone': 'Telefone de Contato',
      'marketplace.contactPhone.placeholder': '(11) 99999-9999',
      'marketplace.description': 'Descrição',
      'marketplace.description.placeholder': 'Descrição do marketplace',
      'marketplace.apiKey': 'Chave da API',
      'marketplace.apiKey.placeholder': 'Chave de acesso à API',
      'marketplace.apiSecret': 'Segredo da API',
      'marketplace.apiSecret.placeholder': 'Segredo de acesso à API',
      'marketplace.shippingTime': 'Tempo de Envio (dias)',
      'marketplace.shippingTime.placeholder': '0',
      'marketplace.shippingMethod': 'Método de Envio',
      'marketplace.shippingMethod.placeholder': 'Correios, Transportadora',
      'marketplace.returnPolicy': 'Política de Devolução',
      'marketplace.returnPolicy.placeholder': '7 dias, 30 dias',
      'marketplace.notes': 'Observações',
      'marketplace.notes.placeholder': 'Observações adicionais',
      'marketplace.active': 'Marketplace Ativo',
      'marketplace.active.yes': 'Sim',
      'marketplace.active.no': 'Não',
      'marketplace.register': 'Cadastrar Marketplace'
    };
    
    // Retorna a tradução se existir, senão retorna a chave original
    return translations[key] || key;
  }
}
